/* eslint-disable react/prop-types */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  Paper,
} from "@mui/material";
import { DragIndicator, Close as CloseIcon, Save } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import StepBasicInfo from "./StepBasicInfo";
import StepInputsSection from "./StepInputsSection";

const DraggablePaper = (props) => {
  return (
    <Paper
      {...props}
      sx={{
        cursor: "move",
        "& .MuiDialogTitle-root": {
          cursor: "move",
          userSelect: "none",
        },
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 24px 56px rgba(0,0,0,0.15), 0 8px 32px rgba(0,0,0,0.1)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
      }}
    />
  );
};

const StepModalDialog = ({
  open,
  onClose,
  modalButton,
  nodeData,
  setNodeData,
  userGroupIds,
  userGroupsList,
  inputTypeList,
  handleMultiChange,
  isFormValid,
  onSave,
  onAddStep,
  // Step inputs handlers
  handleInputTypeChange,
  addNewStepInput,
  handleDeleteStepInput,
  moveStepInputUp,
  moveStepInputDown,
  optionInputs,
  handleOptionInputChange,
  handleAddOption,
  removeOption,
  // Translation prefix for different workflows
  translationPrefix = "newWorkFlow",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isUserGroup, setIsUserGroup] = React.useState(true);
  const [isActionName, setIsActionName] = React.useState(true);
  const [isStatus, setIsStatus] = React.useState(true);

  React.useEffect(() => {
    setIsUserGroup(userGroupIds.length > 0);
    setIsActionName(Boolean(nodeData.action_name));
    setIsStatus(Boolean(nodeData.status));
  }, [userGroupIds.length, nodeData.action_name, nodeData.status]);

  return (
    <Dialog
      maxWidth="xl"
      PaperComponent={DraggablePaper}
      hideBackdrop={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "90vw",
          maxWidth: "1400px",
          minHeight: "80vh",
          maxHeight: "90vh",
          m: 0,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(145deg, ${theme.palette.grey[900]}, ${theme.palette.grey[800]})`
              : `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)",
        },
      }}
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={false}
      aria-labelledby="draggable-step-dialog-title"
    >
      <DialogTitle
        id="draggable-step-dialog-title"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
          fontWeight: 700,
          fontSize: "1.5rem",
          py: 3,
          px: 4,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "100px",
            height: "100px",
            background: `radial-gradient(circle, ${theme.palette.primary.light}40 0%, transparent 70%)`,
            borderRadius: "50%",
            transform: "translate(30%, -30%)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: theme.shadows[3],
            }}
          >
            <DragIndicator sx={{ color: theme.palette.primary.contrastText }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {modalButton === "create"
                ? t(`${translationPrefix}.addStepTitle`)
                : t(`${translationPrefix}.updateStepTitle`)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {modalButton === "create"
                ? t(`${translationPrefix}.addStepSubtitle`) ||
                  "Configure a new workflow step with detailed settings"
                : t(`${translationPrefix}.updateStepSubtitle`) ||
                  "Modify existing workflow step configuration"}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.primary.contrastText,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease",
              zIndex: 1,
            }}
            aria-label={t(`${translationPrefix}.closeModalAria`)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
          backgroundSize: "200% 100%",
          animation: "gradient 3s ease infinite",
          "@keyframes gradient": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      />

      <DialogContent sx={{ p: 3 }}>
        {/* Basic Step Information */}
        <StepBasicInfo
          nodeData={nodeData}
          setNodeData={setNodeData}
          userGroupIds={userGroupIds}
          userGroupsList={userGroupsList}
          handleMultiChange={handleMultiChange}
          isUserGroup={isUserGroup}
          setIsUserGroup={setIsUserGroup}
          isActionName={isActionName}
          setIsActionName={setIsActionName}
          isStatus={isStatus}
          setIsStatus={setIsStatus}
          translationPrefix={translationPrefix}
        />

        {/* Step Inputs Section */}
        <StepInputsSection
          nodeData={nodeData}
          inputTypeList={inputTypeList}
          handleInputTypeChange={handleInputTypeChange}
          addNewStepInput={addNewStepInput}
          handleDeleteStepInput={handleDeleteStepInput}
          moveStepInputUp={moveStepInputUp}
          moveStepInputDown={moveStepInputDown}
          optionInputs={optionInputs}
          handleOptionInputChange={handleOptionInputChange}
          handleAddOption={handleAddOption}
          removeOption={removeOption}
          translationPrefix={translationPrefix}
        />
      </DialogContent>

      <DialogActions sx={{ pr: 6, pb: 3 }}>
        {modalButton === "create" && (
          <Button
            onClick={onAddStep}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={!isFormValid}
            sx={{
              borderRadius: 1,
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                background: theme.palette.primary.dark,
              },
            }}
            aria-label={t(`${translationPrefix}.addStepButtonAria`)}
          >
            {t(`${translationPrefix}.addStepButton`)}
          </Button>
        )}
        {modalButton === "update" && (
          <Button
            onClick={onSave}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={!isFormValid}
            sx={{
              borderRadius: 1,
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                background: theme.palette.primary.dark,
              },
            }}
            aria-label={t(`${translationPrefix}.saveButtonAria`)}
          >
            {t(`${translationPrefix}.saveButton`)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StepModalDialog;
